const SITE = 'https://printablegraphpaperpdf.com';

const BASE = {
  color: '#1a1a1a',
  minorWidth: 0.3,
  majorWidth: 0.8,
  accentEvery: 0,
  orientation: 'portrait',
};

const PRESETS = {
  a4: { ...BASE, type: 'square', spacing: 5, pageSize: 'a4' },
  mm5: { ...BASE, type: 'square', spacing: 5, pageSize: 'letter' },
  dot: { ...BASE, type: 'dot', spacing: 5, pageSize: 'letter' },
  isometric: { ...BASE, type: 'isometric', spacing: 5, pageSize: 'letter' },
  engineering: { ...BASE, type: 'engineering', spacing: 5, pageSize: 'letter' },
  inch1: { ...BASE, type: 'square', spacing: 25.4, pageSize: 'letter' },
  inchHalf: { ...BASE, type: 'square', spacing: 12.7, pageSize: 'letter' },
  inchQuarter: { ...BASE, type: 'square', spacing: 6.35, pageSize: 'letter' },
};

const DOWNLOAD_FORMATS = [
  {
    name: 'PDF',
    title: 'PDF — print-perfect',
    body: 'A vector PDF prints your grid at exactly the size you chose (no stretching or blur). Use it with any printer or PDF app. Pick margins “None” and print at 100% scale.',
  },
  {
    name: 'PNG',
    title: 'PNG — for apps & images',
    body: 'A high-resolution PNG (2× export) is ideal when you want to drop the grid into documents, tablets, drawing apps, or image editors.',
  },
  {
    name: 'SVG',
    title: 'SVG — for design software',
    body: 'A vector SVG opens in Illustrator, Figma, Inkscape, and similar tools, so the grid stays crisp at any zoom and is fully editable.',
  },
];

const FORMAT_INTRO = 'Every grid downloads in three formats, so you can print, paste, or edit it however you like. Files are generated instantly in your browser — no sign-up, no watermark, no limits.';

function sizeCards(entries) {
  return entries.map((e) => ({
    title: e.title,
    body: e.body,
    cta: e.cta || 'Pick this size',
  }));
}

export const LANDING_PAGES = [
  /* ─────────────────────────── A4 ─────────────────────────── */
  {
    path: '/a4-graph-paper',
    h1: 'Free Printable A4 Graph Paper.',
    eyebrow: 'METRIC GRID · 210 × 297 MM · PDF / PNG / SVG',
    metaTitle: 'A4 Graph Paper – Free Printable A4 Grid Paper PDF',
    metaDescription: 'Download free printable A4 graph paper with 5mm squares. Perfect metric grid for math, engineering, and planning. Generate, customize, and print A4 grid paper PDF at true scale.',
    keywords: 'a4 graph paper, printable a4 graph paper, a4 grid paper, a4 graph paper pdf, printable a4 grid paper, graph paper a4 pdf, printable graph paper a4, a4 graph paper printable',
    canonical: `${SITE}/a4-graph-paper`,
    ogUrl: `${SITE}/a4-graph-paper`,
    ctaPrimary: 'Generate your A4 grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.a4,
    intro: [
      'A4 graph paper is the international standard for squared paper — a 210 × 297 mm (8.27 × 11.69 in) sheet used across Europe, Asia, Australia, and most of the world. Its generous, portrait-friendly size makes it the metric answer to US Letter graph paper.',
      'This page gives you a free A4 graph paper generator, preconfigured to a clean 5 mm square grid. Adjust the spacing, line weight, color, or accent lines, then download as PDF, PNG, or SVG — all at true scale, with no watermark and no signup.',
    ],
    facts: [
      { k: 'Grid', v: '5 mm squares' },
      { k: 'Size', v: '210 × 297 mm' },
      { k: 'Format', v: 'PDF · PNG · SVG' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'A4 dimensions & printing',
    dimBody: 'A4 is the most widely used paper size in the metric world. Here is exactly what you get, and how to print it so the squares measure true to size.',
    dims: [
      { k: 'Width', v: '210 mm · 21.0 cm · 8.27 in' },
      { k: 'Height', v: '297 mm · 29.7 cm · 11.69 in' },
      { k: 'Aspect ratio', v: '1 : √2 (≈ 1 : 1.414)' },
      { k: 'Default grid', v: '5 mm squares' },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Which A4 grid size should you pick?',
    sizesBody: 'Stick with the 5 mm preset for everyday work, or switch in one click — the generator updates live.',
    sizes: sizeCards([
      { title: '2 mm', body: 'Ultra-fine grid for detailed technical drawings and dense plotting.' },
      { title: '3 mm', body: 'A tight but readable grid — great for engineering sketches.' },
      { title: '5 mm', body: 'The metric classic. Balanced for math, writing, and drawing.' },
      { title: '1 cm', body: 'Large squares for younger learners and spacious layouts.' },
    ]),
    formatHeading: 'Download your A4 graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape A4',
    orientBody: 'A4 portrait is the default for a reason: the tall 297 mm side gives you 57 rows of 5 mm squares for long equations, full-page plots, and handwriting. Switch to landscape when you want wider rows — a 210 mm tall space is perfect for timeline charts, planning boards, and wide diagrams. Both orientations print at the same true scale.',
    orientPoints: [
      'Portrait — 42 columns × 57 rows of 5 mm squares',
      'Landscape — 57 columns × 42 rows of 5 mm squares',
      'Flip instantly with one button in the orientation control',
    ],
    usesHeading: 'Who uses A4 graph paper?',
    usesBody: 'Because A4 is the standard metric sheet, A4 graph paper shows up everywhere precision matters.',
    uses: [
      { title: 'Math students', body: 'Graphing functions, geometry constructions, and coordinate practice on the international standard.' },
      { title: 'Engineers', body: '5mm metric grids map cleanly to metric measurements, ideal for sketches and notes.' },
      { title: 'Cross-stitch & crafts', body: 'Each 5mm square maps to a stitch or pixel, turning patterns into easy-to-follow charts.' },
      { title: 'Planner & journal users', body: 'A clean metric dot-free grid for layouts, habit trackers, and bullet journal spreads.' },
      { title: 'Technical illustrators', body: 'Isometric and scale drawings start on a grid that reads in millimeters.' },
      { title: 'Students abroad', body: 'If your school, university, or exam board uses A4, this paper matches from the start.' },
    ],
    faqHeading: 'A4 graph paper — frequently asked questions',
    faqs: [
      {
        q: 'What size is A4 graph paper?',
        a: 'A4 paper measures exactly 210 × 297 mm, which is 21.0 × 29.7 cm or 8.27 × 11.69 inches. Our A4 graph paper generator fills that entire sheet with a grid, defaulting to 5 mm squares.',
      },
      {
        q: 'How many 5mm squares fit on an A4 page?',
        a: 'A portrait A4 sheet fits around 42 squares across and 57 squares down at 5 mm spacing — roughly 2,394 squares. At 1 cm spacing that drops to about 21 × 28 squares.',
      },
      {
        q: 'Is A4 graph paper the same as Letter?',
        a: 'No. Letter paper is 8.5 × 11 in (216 × 279 mm), slightly wider and a little shorter than A4. The metric grid sizes (like 5 mm) are designed around A4, while inch grids (1/4, 1/2, 1 inch) suit US Letter paper best.',
      },
      {
        q: 'How do I print A4 graph paper at true scale?',
        a: 'After downloading the PDF, open it in a PDF viewer and print at 100% scale with margins set to None. A 5 mm square will then measure exactly 5 mm on paper. Avoid “Fit to Page”, which rescales the grid.',
      },
      {
        q: 'What is A4 graph paper used for?',
        a: 'Students use it for math and science work, engineers for technical sketches, and crafters for cross-stitch and design charts. It is also a favorite for planners, bullet journals, and exam practice because it matches the paper size used in most schools.',
      },
    ],
    relatedHeading: 'More paper you might need',
    related: [
      { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The same classic metric grid on any page size.' },
      { path: '/dot-grid-paper', title: 'Dot Grid Paper', blurb: 'A clean dot grid, perfect for planners and sketching.' },
      { path: '/isometric-graph-paper', title: 'Isometric Graph Paper', blurb: 'Triangles for 3D drawings on an A4 sheet.' },
      { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'The US equivalent — 4 squares per inch.' },
    ],
  },

  /* ─────────────────────────── 5mm ─────────────────────────── */
  {
    path: '/5mm-graph-paper',
    h1: 'Free Printable 5mm Graph Paper.',
    eyebrow: '5 MM SQUARES · ~5 SQUARES PER INCH · PDF / PNG / SVG',
    metaTitle: '5mm Graph Paper – Free Printable 5mm Grid Squares PDF',
    metaDescription: 'Free printable 5mm graph paper with exact 5mm squares — roughly 5 squares per inch. Generate, download, and print your own 5mm grid paper PDF at true scale, no signup.',
    keywords: '5mm graph paper, printable 5mm graph paper, 5mm grid paper, 5mm graph paper pdf, graph paper 5mm, metric graph paper',
    canonical: `${SITE}/5mm-graph-paper`,
    ogUrl: `${SITE}/5mm-graph-paper`,
    ctaPrimary: 'Generate 5mm grid paper',
    secondaryCta: 'See common uses',
    preset: PRESETS.mm5,
    intro: [
      '5mm graph paper is the workhorse of metric squared paper. Its squares measure exactly 5 millimeters — about 0.2 inches, or roughly five squares per inch — which explains why it is the default grid in millions of classrooms, workshops, and engineering desks.',
      'Use the generator below to produce a clean 5mm grid on Letter or A4, then fine-tune the line color, weight, or add accent lines. Download as a print-perfect PDF, a hi-res PNG, or an editable SVG — free, instant, and true to scale.',
    ],
    facts: [
      { k: 'Square size', v: '5 mm (0.197 in)' },
      { k: 'Squares / inch', v: '≈ 5.08' },
      { k: 'Page options', v: 'Letter or A4' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'The 5mm grid, explained',
    dimBody: 'A 5mm square is small enough for precise plotting yet large enough to write in comfortably. Here is how the math on a standard sheet plays out.',
    dims: [
      { k: 'Square size', v: '5 mm · 0.51 cm · 0.197 in' },
      { k: 'Squares per inch', v: '≈ 5.08' },
      { k: 'On Letter portrait', v: '≈ 43 × 53 squares' },
      { k: 'On A4 portrait', v: '≈ 42 × 57 squares' },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Compare 5mm with other grid sizes',
    sizesBody: '5mm hits the sweet spot for most people. Here is how it stacks up against the alternatives you can switch to in one click.',
    sizes: sizeCards([
      { title: '2 mm', body: 'Much finer — two and a half sub-grids inside one 5mm square. For dense detail work.' },
      { title: '5 mm', body: 'The default choice: readable, precise, and universally useful. You are already here.' },
      { title: '10 mm (1 cm)', body: 'Double the size — each square is one centimeter, great for kids and large layouts.' },
      { title: '¼ inch', body: 'A US-school classic at 6.35 mm, slightly larger than 5mm squares.' },
    ]),
    formatHeading: 'Download & print 5mm graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape 5mm',
    orientBody: 'The 5mm grid works beautifully in both orientations. Portrait gives tall columns for vertical graphs and long calculations; landscape spreads the same grid wider for timeline charts and engineering schematics. Because squares are square, you lose nothing by rotating — only the row and column counts trade places.',
    orientPoints: [
      'Portrait — more rows for vertical work and lists',
      'Landscape — wider rows for charts and diagrams',
      'Both keep exact 5mm squares at print time',
    ],
    usesHeading: 'What is 5mm graph paper good for?',
    usesBody: '5mm is the default metric grid, so it appears in everything from primary school to professional practice.',
    uses: [
      { title: 'Math & science class', body: 'The standard grid for graphing functions, plotting data, and geometry — in metric countries, this is the default.' },
      { title: 'Engineering notes', body: 'Metric sketches and design calculations stay consistent when the grid reads in millimeters.' },
      { title: 'Bullet journaling', body: 'A structured but subtle grid for monthly spreads, trackers, and rapid logging.' },
      { title: 'Cross-stitch & pixel art', body: 'Each 5mm cell maps cleanly to one stitch, one bead, or one pixel block.' },
      { title: 'Wireframing', body: 'Enough structure to align UI blocks without the heaviness of a 1cm grid.' },
      { title: 'Exam & homework prep', body: 'Neat, uniform squares keep handwritten math organized and easy to read.' },
    ],
    faqHeading: '5mm graph paper — frequently asked questions',
    faqs: [
      {
        q: 'How big is a 5mm square on paper?',
        a: 'Exactly 5 millimeters, which is 0.5 centimeters or about 0.197 inches. Printed at 100% scale, our grid is dimensionally accurate, so roughly five squares fit per inch.',
      },
      {
        q: 'How many 5mm squares fit on a page?',
        a: 'On US Letter portrait there are about 43 across and 53 down. On A4 portrait: about 42 across and 57 down. In landscape, those counts swap.',
      },
      {
        q: 'Is 5mm graph paper the same as 1/4 inch graph paper?',
        a: 'Not quite. 1/4 inch equals 6.35 mm, so a quarter-inch square is about 27% larger than a 5mm square. If you need true imperial fractions, use the 1/4, 1/2, or 1 inch grids instead.',
      },
      {
        q: 'Can I print 5mm graph paper on Letter and A4?',
        a: 'Yes. The generator lets you switch page size between Letter and A4 (plus Legal, A3, A2, and more) at any time. The 5mm squares stay exactly 5mm on whichever sheet you choose.',
      },
      {
        q: 'How do I make sure 5mm is printed at true scale?',
        a: 'Download the PDF and print at 100% scale with margins set to None. If a printed square is not exactly 5mm, your printer was set to “Fit to Page” — reset it to “Actual Size”.',
      },
    ],
    relatedHeading: 'Grids that pair well with 5mm',
    related: [
      { path: '/a4-graph-paper', title: 'A4 Graph Paper', blurb: 'The same metric grid on the international A4 sheet.' },
      { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: '5mm minor lines with a bold major line every five.' },
      { path: '/dot-grid-paper', title: 'Dot Grid Paper', blurb: 'Squares traded for dots at the same 5mm pitch.' },
      { path: '/1-2-inch-graph-paper', title: '1/2 Inch Graph Paper', blurb: 'A generous US grid at 12.7mm spacing.' },
    ],
  },

  /* ─────────────────────────── Dot grid ─────────────────────────── */
  {
    path: '/dot-grid-paper',
    h1: 'Free Printable Dot Grid Paper.',
    eyebrow: 'DOT GRID · 5 MM PITCH · BULLET JOURNAL & DESIGN READY',
    metaTitle: 'Dot Grid Paper – Free Printable Dot Grid PDF | Bullet Journal',
    metaDescription: 'Free printable dot grid paper in a clean 5mm dot matrix. Perfect for bullet journals, planners, sketching, and UI wireframes. Download dot grid paper PDF, PNG, or SVG free.',
    keywords: 'dot grid paper, printable dot grid, bullet journal paper, dot matrix paper, printable dot grid paper, free dot grid paper, dot grid paper printable, free printable dot grid paper, bullet journal paper printable',
    canonical: `${SITE}/dot-grid-paper`,
    ogUrl: `${SITE}/dot-grid-paper`,
    ctaPrimary: 'Generate dot grid paper',
    secondaryCta: 'See common uses',
    preset: PRESETS.dot,
    intro: [
      'Dot grid paper replaces the lines and squares of classic graph paper with a matrix of subtle dots. You get all the structure of a grid for alignment — with none of the visual noise — which is exactly why bullet journalists, planners, UX designers, and artists swear by it.',
      'This generator produces a crisp 5 mm dot grid, ready for any page size you choose. Adjust the dot size, color, or spacing, then download as a print-perfect PDF, high-res PNG, or editable SVG. Free, no watermark, no signup.',
    ],
    facts: [
      { k: 'Dot pitch', v: '5 mm apart' },
      { k: 'Style', v: 'Subtle round dots' },
      { k: 'Best for', v: 'Journals & wireframes' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'Dot grid size & structure',
    dimBody: 'Dot paper is defined by its pitch — the distance between dots. The 5 mm default is the bullet-journaling standard, but you can dial it up or down to taste.',
    dims: [
      { k: 'Default pitch', v: '5 mm between dots' },
      { k: 'On Letter portrait', v: '≈ 43 × 53 dots' },
      { k: 'On A4 portrait', v: '≈ 42 × 57 dots' },
      { k: 'Dot size', v: 'Adjustable (weight slider)' },
      { k: 'Color', v: 'Black, gray, blue & more' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Choose your dot density',
    sizesBody: 'The right pitch depends on how you write, draw, and plan. All sizes update live in the generator.',
    sizes: sizeCards([
      { title: '3 mm', body: 'Tight dots for small handwriting, detailed sketches, and dense layouts.' },
      { title: '5 mm', body: 'The journal standard — matches most bullet journal setups and drop-in stencils.' },
      { title: '7.5 mm', body: 'Roomier dots for large handwriting, mind maps, and loose creative work.' },
      { title: '1 cm', body: 'Big dots for kids, brainstorming boards, and poster-sized layouts.' },
    ]),
    formatHeading: 'Download & print dot grid paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape dots',
    orientBody: 'Portrait dot grid suits journaling, planners, and note-taking — the tall format matches how we write pages. Landscape dot grid is a favorite for wireframing screens, sketching wide scenes, and laying out spreads that span two pages. The dots stay perfectly aligned either way.',
    orientPoints: [
      'Portrait — the classic journal and planner orientation',
      'Landscape — wider canvas for wireframes and sketches',
      'Perfectly regular dot matrix in both orientations',
    ],
    usesHeading: 'What is dot grid paper used for?',
    usesBody: 'A tiny change — lines to dots — unlocks a surprising range of uses.',
    uses: [
      { title: 'Bullet journaling', body: 'The signature dot grid: index pages, monthly logs, habit trackers, and collections, all perfectly aligned.' },
      { title: 'UX & UI wireframing', body: 'Sketch screens, cards, and layout blocks using dots as silent alignment guides.' },
      { title: 'Sketching & drawing', body: 'Achieve straight lines and even proportions without grid lines confusing your artwork.' },
      { title: 'Lettering & calligraphy', body: 'Dots help you keep letter heights and baselines consistent without visible ruling.' },
      { title: 'Architectural quick sketches', body: 'Draw floor plans and elevations to scale with subtle guidance.' },
      { title: 'School & study', body: 'Grids for math, tables, and graphs that stay clean enough for neat handwriting.' },
    ],
    faqHeading: 'Dot grid paper — frequently asked questions',
    faqs: [
      {
        q: 'What is dot grid paper?',
        a: 'Dot grid paper is paper printed with a regular matrix of small dots instead of continuous lines or solid squares. The dots give you a guide for alignment and measurement while leaving the page visually clean.',
      },
      {
        q: 'What is the difference between dot grid and graph paper?',
        a: 'Graph paper uses full perpendicular lines that form squares, which can feel busy and restrictive. Dot grid paper uses only dots at the same intersections, so you can draw freely — letters, sketches, layouts — while staying aligned.',
      },
      {
        q: 'What dot spacing is best for bullet journals?',
        a: '5 mm is the standard dot grid spacing used by most pre-printed bullet journals. It suits most handwriting sizes. If you write large, try 7.5 mm; for tiny handwriting, 3 mm keeps things tidy.',
      },
      {
        q: 'Can I print dot grid paper for free?',
        a: 'Yes. This generator is completely free — no signup, no watermark, no usage limits. Download as many dot grid PDFs as you like in any dot pitch and page size.',
      },
      {
        q: 'Is dot grid paper good for drawing?',
        a: 'Very. Dots provide structure for symmetry, perspective, and straight edges while staying out of the way of finished artwork — a big reason illustrators and letterers prefer it over lined or graph paper.',
      },
    ],
    relatedHeading: 'Paper to pair with dot grid',
    related: [
      { path: '/a4-graph-paper', title: 'A4 Graph Paper', blurb: 'Squared guidance when you need full grid lines.' },
      { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The same 5mm pitch, drawn as solid squares.' },
      { path: '/isometric-graph-paper', title: 'Isometric Graph Paper', blurb: 'Dot-free triangles for 3D and perspective work.' },
      { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'Imperial grid squares for US school math.' },
    ],
  },

  /* ─────────────────────────── Isometric ─────────────────────────── */
  {
    path: '/isometric-graph-paper',
    h1: 'Free Printable Isometric Graph Paper.',
    eyebrow: '30° TRIANGULAR GRID · 3D & PERSPECTIVE READY · PDF / PNG / SVG',
    metaTitle: 'Isometric Graph Paper – Free Printable Isometric Grid PDF',
    metaDescription: 'Free printable isometric graph paper for 3D drawings, perspective sketches, and engineering diagrams. Generate and download isometric grid paper PDF, PNG, or SVG at exact scale.',
    keywords: 'isometric graph paper, isometric paper, printable isometric graph paper, isometric grid paper, isometric drawing paper, free isometric paper, isometric graph paper printable, isometric graph paper pdf, isometric paper printable, printable isometric paper, isometric paper printable pdf',
    canonical: `${SITE}/isometric-graph-paper`,
    ogUrl: `${SITE}/isometric-graph-paper`,
    ctaPrimary: 'Generate isometric grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.isometric,
    intro: [
      'Isometric graph paper uses a grid of equilateral triangles instead of squares. When you draw along its three diagonal axes you get a convincing 3D view — all three faces of a cube visible at once — which makes it the go-to paper for isometric drawing, perspective sketches, and technical illustration.',
      'The generator below produces a clean 5 mm isometric grid with the classic 30° diagonal pattern. Set your own grid size, line weight, and color, then export as PDF, PNG, or SVG — free and accurate to the millimeter.',
    ],
    facts: [
      { k: 'Angle', v: '30° diagonals' },
      { k: 'Shape', v: 'Equilateral triangles' },
      { k: 'Grid', v: '5 mm spacing' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'How isometric grids work',
    dimBody: 'Every line on this paper runs at 0°, 60°, or 120° — the three axes of isometric projection. That guarantees parallel edges and true angles as you sketch.',
    dims: [
      { k: 'Line angles', v: '0° / 60° / 120°' },
      { k: 'Triangle type', v: 'Equilateral' },
      { k: 'Default spacing', v: '5 mm between lines' },
      { k: 'On Letter portrait', v: 'Fills the full sheet' },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Pick your isometric density',
    sizesBody: 'Fine grids suit small objects and hand-sized drawings; larger grids speed up bold, chunky sketches. Switch sizes live.',
    sizes: sizeCards([
      { title: '3 mm', body: 'Tight triangles for detailed isometric drawing and small component sketches.' },
      { title: '5 mm', body: 'The balanced default — clean cubes and manageable triangle density.' },
      { title: '7.5 mm', body: 'Roomier spacing for quicker, more expressive sketches.' },
      { title: '1 cm', body: 'Bold triangles for teaching perspective and large concept drawings.' },
    ]),
    formatHeading: 'Download & print isometric graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape isometric',
    orientBody: 'Both orientations preserve the three 30° axes perfectly. Portrait sheets suit vertical compositions like characters, buildings, and stacked objects. Landscape sheets give more horizontal travel for scenes, floor plans, and exploded views. Because isometric projection is rotation-independent, converting between them is just a button press.',
    orientPoints: [
      'Portrait — vertical compositions, towers, characters',
      'Landscape — wide scenes, layouts, exploded views',
      'Axes stay at true 30° in both orientations',
    ],
    usesHeading: 'What is isometric paper used for?',
    usesBody: 'Isometric paper sits at the intersection of art, math, and engineering.',
    uses: [
      { title: 'Isometric drawing', body: 'Sketch cubes, prisms, and assemblies where all three faces are visible — the classic technical-art exercise.' },
      { title: 'Video game art', body: 'Concept designers and pixel artists use isometric grids for buildable, tile-friendly game scenes.' },
      { title: 'Engineering sketches', body: 'Communicate three dimensions quickly on paper before moving to CAD.' },
      { title: 'Architecture', body: 'Exploded views and spatial sketches of rooms and structures.' },
      { title: 'Math classrooms', body: 'Visualize 3D shapes, volume, and spatial reasoning with a grid that makes depth tangible.' },
      { title: 'Product design', body: 'Early-stage form studies and industrial design thumbnails in perspective.' },
    ],
    faqHeading: 'Isometric graph paper — frequently asked questions',
    faqs: [
      {
        q: 'What is isometric graph paper?',
        a: 'Isometric graph paper is printed with a triangular grid of lines at 0°, 60°, and 120°. Drawing along these three axes produces the classic isometric 3D view, where you see three sides of an object at once.',
      },
      {
        q: 'What angle is the isometric grid?',
        a: 'The diagonal lines run at exactly 30° to the horizontal, forming equilateral triangles. Two of the three line families slope at 30°, while the third runs straight up and down.',
      },
      {
        q: 'How do I use isometric paper?',
        a: 'Draw vertical edges along the vertical lines and depth/width edges along the two diagonal directions. Keep parallel edges parallel and follow the pre-printed lines — that technique alone gives a correct 3D look.',
      },
      {
        q: 'What is isometric paper used for in school?',
        a: 'Students use it in geometry, art, and design & technology classes to visualize 3D shapes, draw nets and prisms, and practice perspective drawing before moving to technical software.',
      },
      {
        q: 'Can I change the isometric grid size?',
        a: 'Yes. The generator lets you switch between 3mm, 5mm, 7.5mm, and 1cm spacing (or paint a custom value), and it previews the change instantly before you download.',
      },
    ],
    relatedHeading: 'Related grids to explore',
    related: [
      { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: 'Traditional squares with heavy major lines for technical work.' },
      { path: '/dot-grid-paper', title: 'Dot Grid Paper', blurb: 'Subtle dots for freehand 3D sketching.' },
      { path: '/a4-graph-paper', title: 'A4 Graph Paper', blurb: 'Featherweight metric squares for isometric cube math.' },
      { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The classic square grid that started it all.' },
    ],
  },

  /* ─────────────────────────── Engineering ─────────────────────────── */
  {
    path: '/engineering-graph-paper',
    h1: 'Free Printable Engineering Graph Paper.',
    eyebrow: 'MINOR GRID · BOLD MAJOR LINE EVERY 5 · PDF / PNG / SVG',
    metaTitle: 'Engineering Graph Paper – Free Printable Engineering Grid PDF',
    metaDescription: 'Free printable engineering graph paper with thin grid lines and a bold major line every 5 squares. Ideal for engineering sketches, math, and technical drawing. Download PDF, PNG, or SVG.',
    keywords: 'engineering graph paper, engineering paper, printable engineering graph paper, engineering grid paper, engineering paper printable, engineering graph paper pdf, engineering graph paper printable, engineering paper pdf',
    canonical: `${SITE}/engineering-graph-paper`,
    ogUrl: `${SITE}/engineering-graph-paper`,
    ctaPrimary: 'Generate engineering grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.engineering,
    intro: [
      'Engineering graph paper features a fine grid of thin lines with a heavier major line every five squares. The thickened 5-line pattern makes it trivial to count cells, keep sketches proportional, and read coordinates at a glance — a small upgrade that engineers, architects, and math teachers rely on.',
      'This generator preconfigures the classic engineering setup: thin 5 mm minor lines with a bold line every fifth cell. Tweak line weights, colors, or page size, then export as PDF, PNG, or SVG — free at true scale.',
    ],
    facts: [
      { k: 'Major line', v: 'Every 5 squares' },
      { k: 'Minor line', v: 'Thin, light' },
      { k: 'Default grid', v: '5 mm squares' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'Engineering grid anatomy',
    dimBody: 'Two line weights create a visual ruler: count five of the thin lines and a bold line anchors your place.',
    dims: [
      { k: 'Minor spacing', v: '5 mm', },
      { k: 'Major interval', v: 'Every 5 squares (25 mm)' },
      { k: 'Minor line weight', v: '0.3 px (adjustable)' },
      { k: 'Major line weight', v: '0.8 px (adjustable)' },
      { k: 'On Letter portrait', v: '≈ 43 × 53 squares' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Grid sizes for engineering paper',
    sizesBody: 'Any of these work in the generator — the bold 5-square major line stays anchored to your chosen spacing.',
    sizes: sizeCards([
      { title: '3 mm', body: 'Tight minor grid for compact schematic sketching and dense math.' },
      { title: '5 mm', body: 'The standard engineering setup — the preset you see above.' },
      { title: '7.5 mm', body: 'Relaxed spacing for hand-worked layouts and annotated drawings.' },
      { title: '¼ inch', body: 'Imperial engineering classic at 4 squares per inch.' },
    ]),
    formatHeading: 'Download & print engineering graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape engineering',
    orientBody: 'Engineering practice uses both. Portrait suits written calculations and column-style math where you read top to bottom. Landscape spreads wide for orthographic projections, floor plans, and graph plots. The bold 5-line rhythm persists in either orientation, so counting never slows down.',
    orientPoints: [
      'Portrait — calculations, notes, tall plots',
      'Landscape — plans, projections, wide diagrams',
      'Major-line rhythm identical in both orientations',
    ],
    usesHeading: 'Who uses engineering graph paper?',
    usesBody: 'Name any discipline that sketches to scale and engineering paper is probably in the drawer.',
    uses: [
      { title: 'Engineering students', body: 'Schematic notes, shear-and-moment diagrams, and design workbooks that stay organized.' },
      { title: 'Math classes', body: 'The bold 5-line makes reading graphs, intervals, and coordinates dramatically easier for learners.' },
      { title: 'Architecture', body: 'Floor-plan sketches and scaled hand drafts on a grid engineered for counting.' },
      { title: 'Technical illustrators', body: 'Clean orthographic and isometric diagrams with an effortless measurement reference.' },
      { title: 'Science labs', body: 'Plotting experimental data by hand where precision counting matters.' },
      { title: 'Woodworkers & makers', body: 'Scale drawings and cut lists sketched on a grid that reads like a ruler.' },
    ],
    faqHeading: 'Engineering graph paper — frequently asked questions',
    faqs: [
      {
        q: 'What is engineering graph paper?',
        a: 'Engineering graph paper is printed with a fine minor grid plus a heavier major line every five squares. The bold 5-square pattern acts as a built-in ruler, making it faster to count cells and keep drawings proportional.',
      },
      {
        q: 'What is the difference between engineering paper and regular graph paper?',
        a: 'Regular graph paper uses a single line weight, so counting squares means tracking every line. Engineering paper bumps the line every fifth square to a heavier weight, so you can jump five cells at a glance.',
      },
      {
        q: 'What grid size is standard for engineering paper?',
        a: 'The most common is a 5 mm minor grid (about five squares per inch) with a major line every 5 squares. Imperial versions use 1/4 inch minor squares (4 per inch). Our generator supports both families.',
      },
      {
        q: 'Why are the major lines thicker on engineering paper?',
        a: 'Thicker major lines create a reference grid every 5 squares, so you can read coordinates and count subdivisions quickly without losing your place — essential when sketching technical diagrams by hand.',
      },
      {
        q: 'How do I print engineering graph paper at the right scale?',
        a: 'Download the PDF and print at 100% scale with margins None. A 5mm minor square will measure exactly 5mm, and the major line will land exactly every 25mm.',
      },
    ],
    relatedHeading: 'Complementary paper types',
    related: [
      { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'The imperial engineering classic at 4 squares per inch.' },
      { path: '/1-2-inch-graph-paper', title: '1/2 Inch Graph Paper', blurb: 'A more generous grid (2 squares per inch) for wide strokes.' },
      { path: '/isometric-graph-paper', title: 'Isometric Graph Paper', blurb: 'Triangular grids for 3D engineering views.' },
      { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The same minor grid without the bold majors.' },
    ],
  },

  /* ─────────────────────────── 1 inch ─────────────────────────── */
  {
    path: '/1-inch-graph-paper',
    h1: 'Free Printable 1 Inch Graph Paper.',
    eyebrow: '1″ SQUARES · BIG CELLS · KIDS & LAYOUTS · PDF / PNG / SVG',
    metaTitle: '1 Inch Graph Paper – Free Printable Large Grid PDF',
    metaDescription: 'Free printable 1 inch graph paper with big squares — one cell per inch. Perfect for young learners, posters, and large layouts. Generate and download 1 inch grid paper PDF, PNG, or SVG.',
    keywords: '1 inch graph paper, 1 inch grid paper, printable 1 inch graph paper, large graph paper, free 1 inch graph paper, 1 inch graph paper printable, 1 inch grid paper printable, 1 inch grid paper pdf, 1 inch grid paper printable pdf',
    canonical: `${SITE}/1-inch-graph-paper`,
    ogUrl: `${SITE}/1-inch-graph-paper`,
    ctaPrimary: 'Generate 1-inch grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.inch1,
    intro: [
      '1 inch graph paper is the biggest common grid — one square per inch, which gives a standard Letter sheet just 34 × 44 cells of space. Its oversized squares are made for young hands just learning to write, for tiling large layouts, and for any project where a centimeter of clear space beats packing in hundreds of tiny cells.',
      'The generator here is preconfigured to exact 1-inch squares on US Letter. Change the color, line weight, or page size, then download as PDF, PNG, or SVG at true scale — free, no signup.',
    ],
    facts: [
      { k: 'Square size', v: '1 inch (25.4 mm)' },
      { k: 'Squares / inch', v: '1' },
      { k: 'On Letter', v: '8 × 11 squares*' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'The 1-inch grid in numbers',
    dimBody: 'One square per inch keeps the math trivial. Here is what that means on a standard US Letter page (8.5 × 11 in).',
    dims: [
      { k: 'Square size', v: '25.4 mm · 1 in' },
      { k: 'Squares across Letter', v: '8 (8.5 in usable)' },
      { k: 'Squares down Letter', v: '11' },
      { k: 'Total squares', v: '88 per page', },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: '1 inch vs. the smaller grids',
    sizesBody: 'Big and small grids serve different purposes. Compare before you print.',
    sizes: sizeCards([
      { title: '1 inch', body: 'One cell per inch — maximum readability, minimum cells. Ideal for young learners and poster layouts.' },
      { title: '½ inch', body: '2 squares per inch (12.7 mm) — a middle ground for general drawing and middle-school math.' },
      { title: '¼ inch', body: '4 squares per inch (6.35 mm) — the school-workhorse grid for plotting and coordinate work.' },
      { title: '5 mm', body: '≈ 5 squares per inch — the fine metric alternative for detailed work.' },
    ]),
    formatHeading: 'Download & print 1 inch graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape 1-inch grid',
    orientBody: 'Portrait 1-inch paper gives 8 columns by 11 rows — suited to vertical lists, bingo boards, and page-scale artwork. Landscape flips to 11 across by 8 down, ideal for tic-tac-toe grids, horizontal timelines, and wall-planning boards. Either way, every square is exactly one inch.',
    orientPoints: [
      'Portrait — 8 × 11 squares',
      'Landscape — 11 × 8 squares',
      'Exact 1-inch cells in both orientations',
    ],
    usesHeading: 'What is 1 inch graph paper used for?',
    usesBody: 'Big squares invite big ideas — from kindergarten classrooms to planning boards.',
    uses: [
      { title: 'Early math', body: 'Tracing numbers, counting, and place value — one object or stroke per big square.' },
      { title: 'Letter formation', body: 'First writing practice with room for large, deliberate letterforms.' },
      { title: 'Board games', body: 'Tic-tac-toe, bingo cards, and game-board layouts made in minutes.' },
      { title: 'Posters & artwork', body: 'Planning compositions square-by-square or upscaling pixel art to wall size.' },
      { title: 'Floor planning', body: 'Sketching rough room layouts where 1 inch can stand in for 1 foot.' },
      { title: 'Quilting & cross-stitch', body: 'Chunky pattern charts where each cell maps to a block or stitch.' },
    ],
    faqHeading: '1 inch graph paper — frequently asked questions',
    faqs: [
      {
        q: 'What is 1 inch graph paper?',
        a: '1 inch graph paper is printed with square cells measuring exactly 1 inch (25.4 mm) on each side. On US Letter you get an 8 × 11 grid — 88 large, easy-to-see squares.',
      },
      {
        q: 'How many 1-inch squares are on a page?',
        a: 'A standard Letter sheet (8.5 × 11 in) fits 8 squares across and 11 squares down when printed at true scale with near-zero margins, for 88 squares total. To get a perfect 8 × 11 with clean edges, set your printer margins to None.',
      },
      {
        q: 'Is 1 inch graph paper good for kids?',
        a: 'Yes — large squares are the standard choice for early learners. Big cells give small hands room for letter formation, counting, and art projects without the frustration of cramped 1/4-inch grids.',
      },
      {
        q: 'How do I print 1 inch graph paper at exact size?',
        a: 'Open the downloaded PDF and print at 100% scale with margins None. Set your printer to “Actual Size”, never “Fit to Page”, or the squares will shrink.',
      },
      {
        q: 'Can I use 1 inch graph paper for floor plans?',
        a: 'Yes. Many hobbyists sketch room layouts on 1-inch grid paper using one square to represent one foot (a common blueprint convention), or one square per half-meter for metric plans.',
      },
    ],
    relatedHeading: 'Other sizes in the family',
    related: [
      { path: '/1-2-inch-graph-paper', title: '1/2 Inch Graph Paper', blurb: 'Two squares per inch — the all-round drawing grid.' },
      { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'Four squares per inch — the classic school grid.' },
      { path: '/a4-graph-paper', title: 'A4 Graph Paper', blurb: 'Metric sizes on international paper.' },
      { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: 'Fine lines with bold every-five anchors.' },
    ],
  },

  /* ─────────────────────────── 1/2 inch ─────────────────────────── */
  {
    path: '/1-2-inch-graph-paper',
    h1: 'Free Printable 1/2 Inch Graph Paper.',
    eyebrow: '½″ SQUARES · 2 PER INCH · DRAWING & MIDDLE-SCHOOL · PDF / PNG / SVG',
    metaTitle: '1/2 Inch Graph Paper – Free Printable Half Inch Grid PDF',
    metaDescription: 'Free printable 1/2 inch graph paper with squares of exactly half an inch (2 per inch). A versatile mid-size grid for drawing, math, and planning. Download PDF, PNG, or SVG free.',
    keywords: '1/2 inch graph paper, half inch graph paper, 1/2 inch grid paper, printable 1/2 inch graph paper, graph paper 1/2 inch, 2 squares per inch graph paper, half inch grid paper, free printable graph paper 1/2 inch',
    canonical: `${SITE}/1-2-inch-graph-paper`,
    ogUrl: `${SITE}/1-2-inch-graph-paper`,
    ctaPrimary: 'Generate ½-inch grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.inchHalf,
    intro: [
      '1/2 inch graph paper splits the difference between the fine grids of engineering work and the oversized cells of kindergarten paper. At exactly 12.7 mm per square — two squares to each inch — it is big enough to draw into, yet precise enough for real plotting.',
      'This page preloads the generator with true half-inch squares on US Letter. Adjust spacing, line weight, color, or accent lines, and download as PDF, PNG, or SVG at correct scale. Free, with no registration.',
    ],
    facts: [
      { k: 'Square size', v: '12.7 mm (½″)' },
      { k: 'Squares / inch', v: '2' },
      { k: 'On Letter', v: '17 × 22 squares' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'The ½-inch grid, quantified',
    dimBody: 'Half-inch squares give you a manageable cell count with plenty of writing room per cell.',
    dims: [
      { k: 'Square size', v: '12.7 mm · 0.5 in' },
      { k: 'Squares across Letter', v: '17' },
      { k: 'Squares down Letter', v: '22' },
      { k: 'Total squares', v: '≈ 374 per page' },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Half-inch vs. the neighbors',
    sizesBody: 'Not sure between ¼, ½, and 1 inch? Here is how they compare.',
    sizes: sizeCards([
      { title: '¼ inch', body: '4 per inch — denser and older-kid friendly; better for coordinate plotting.' },
      { title: '½ inch', body: '2 per inch — the balance point. Big enough to draw in, small enough to plot.' },
      { title: '1 inch', body: '1 per inch — maximum air for younger learners and poster layouts.' },
      { title: '5 mm', body: '≈ 5 per inch — the tight metric alternative for fine detail.' },
    ]),
    formatHeading: 'Download & print ½ inch graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape half-inch',
    orientBody: 'Portrait half-inch paper yields 17 columns by 22 rows — a full page of medium cells that still feels airy. Landscape yields 22 columns by 17 rows, great for timeline-style layouts and wide spreadsheets. Your ½-inch squares stay exactly ½ inch in either aspect.',
    orientPoints: [
      'Portrait — 17 × 22 cells of writing-friendly grid',
      'Landscape — 22 × 17 cells for wide layouts',
      'Cells measure true 0.5 in in both orientations',
    ],
    usesHeading: 'What is 1/2 inch graph paper used for?',
    usesBody: 'It is the "just right" grid — structured enough for precision, open enough for creativity.',
    uses: [
      { title: 'Middle-school math', body: 'Graphing, coordinate planes, and geometry exercises with cells large enough to work in.' },
      { title: 'Sketching & doodling', body: 'Half-inch squares give drawing prompts and cross designs room to breathe.' },
      { title: 'Graphic design thumbnails', body: 'Layout boxes, logotype grid work, and composition studies at a comfortable size.' },
      { title: 'Planners & trackers', body: 'Large-scale monthly grids, habit squares, and to-do matrices you can actually write in.' },
      { title: 'Cross-stitch patterns', body: 'A chunky 2-per-inch cell maps cleanly to large or string-art designs.' },
      { title: 'Woodworking & hobby', body: 'Hand-scaled drawings where the half-inch grid reads like a built-in scale.' },
    ],
    faqHeading: '1/2 inch graph paper — frequently asked questions',
    faqs: [
      {
        q: 'How many squares per inch on 1/2 inch graph paper?',
        a: 'Exactly two. Each square measures half an inch (12.7 mm), so a one-inch length spans two squares. A Letter page fits about 17 × 22 squares at true scale.',
      },
      {
        q: 'Is 1/2 inch graph paper good for drawing?',
        a: 'Yes. The cells are large enough to draw simple shapes and characters inside while still giving you a frame for symmetry and proportions. It is a favorite for doodle studies, design thumbnails, and geometric art.',
      },
      {
        q: 'What is the difference between 1/2 inch and 1/4 inch graph paper?',
        a: '1/2 inch paper has squares four times larger in area than 1/4 inch, and offers 2 squares per inch instead of 4. The half-inch grid is roomier and easier on the eyes; the quarter-inch is denser for detailed plotting.',
      },
      {
        q: 'Can I print 1/2 inch graph paper at exactly half-inch squares?',
        a: 'Yes. Download the PDF and print at 100% scale with margins None and “Actual Size” enabled. Each cell will then measure exactly 0.5 inches, giving you a true 17 × 22 grid on Letter.',
      },
      {
        q: 'What else can I do with half-inch graph paper?',
        a: 'Beyond math, people use it for handwritten planners, game boards (through 17 × 22), elementary design class, pixel-art planning, and scale sketching for crafts and small woodworking projects.',
      },
    ],
    relatedHeading: 'Picking the perfect grid size',
    related: [
      { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'Denser 4-per-inch grid for plotting and engineering.' },
      { path: '/1-inch-graph-paper', title: '1 Inch Graph Paper', blurb: 'Bigger cells for young learners and large layouts.' },
      { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: 'Fine grid with bold every-5 anchoring lines.' },
      { path: '/dot-grid-paper', title: 'Dot Grid Paper', blurb: 'Guide dots instead of squares for freeform work.' },
    ],
  },

  /* ─────────────────────────── 1/4 inch ─────────────────────────── */
  {
    path: '/1-4-inch-graph-paper',
    h1: 'Free Printable 1/4 Inch Graph Paper.',
    eyebrow: '¼″ SQUARES · 4 PER INCH · THE CLASSIC SCHOOL GRID · PDF / PNG / SVG',
    metaTitle: '1/4 Inch Graph Paper – Free Printable Quarter Inch Grid PDF',
    metaDescription: 'Free printable 1/4 inch graph paper — the classic 4-squares-per-inch grid for math, engineering sketches, and coordinate plotting. Generate and download quarter-inch grid paper PDF, PNG, or SVG.',
    keywords: '1/4 inch graph paper, quarter inch graph paper, 1/4 inch grid paper, printable 1/4 inch graph paper, graph paper 1/4 inch, 4 squares per inch graph paper, printable graph paper 1/4 inch',
    canonical: `${SITE}/1-4-inch-graph-paper`,
    ogUrl: `${SITE}/1-4-inch-graph-paper`,
    ctaPrimary: 'Generate ¼-inch grid',
    secondaryCta: 'See common uses',
    preset: PRESETS.inchQuarter,
    intro: [
      '1/4 inch graph paper is the most recognizable grid in schools. At four squares per inch (6.35 mm), it packs a full sheet with a grid that is dense enough for real plotting yet clean enough to read — the reason it has been the default for math homework for generations.',
      'The generator below is preconfigured to exact quarter-inch squares on US Letter. You can thicken or lighten lines, shift the color, or add accent lines before downloading as PDF, PNG, or SVG — free, print-true, and no account required.',
    ],
    facts: [
      { k: 'Square size', v: '6.35 mm (¼″)' },
      { k: 'Squares / inch', v: '4' },
      { k: 'On Letter', v: '34 × 44 squares' },
      { k: 'Price', v: 'Free forever' },
    ],
    dimHeading: 'The quarter-inch grid in numbers',
    dimBody: 'Four squares per inch is the sweet spot for classroom math and drafting. Here is the geometry on a Letter sheet.',
    dims: [
      { k: 'Square size', v: '6.35 mm · 0.25 in' },
      { k: 'Squares across Letter', v: '34' },
      { k: 'Squares down Letter', v: '44' },
      { k: 'Total squares', v: '≈ 1,496 per page' },
      { k: 'Resolution', v: '96 DPI (true to size)' },
      { k: 'Print tip', v: 'Scale 100%, margins none' },
    ],
    sizesHeading: 'Quarter-inch vs other grids',
    sizesBody: 'If you are between sizes, here is how ¼ inch compares with its neighbors.',
    sizes: sizeCards([
      { title: '¼ inch', body: '4 per inch — the plotting workhorse used for math and engineering sketches.' },
      { title: '½ inch', body: '2 per inch — larger cells for drawing and younger students.' },
      { title: '1 inch', body: '1 per inch — oversized cells for early learners and posters.' },
      { title: '5 mm', body: '≈ 5 per inch — a slightly finer metric alternative.' },
    ]),
    formatHeading: 'Download & print ¼ inch graph paper',
    formatBody: FORMAT_INTRO,
    formats: DOWNLOAD_FORMATS,
    orientHeading: 'Portrait or landscape quarter-inch',
    orientBody: 'Portrait quarter-inch paper gives 34 columns by 44 rows — a full page of tight, readable cells for exams and worksheets. Landscape gives 44 columns by 34 rows, ideal for wide graph plots and coordinate axes that travel horizontally. Both keep exactly four squares per inch.',
    orientPoints: [
      'Portrait — 34 × 44 cells',
      'Landscape — 44 × 34 cells',
      'Exactly 4 squares per inch in either orientation',
    ],
    usesHeading: 'What is 1/4 inch graph paper used for?',
    usesBody: 'If there is a blank piece of classic grid paper in your classroom, it is almost certainly this size.',
    uses: [
      { title: 'Math homework', body: 'Coordinate planes, graphing functions, and geometry constructions — the standard classroom grid.' },
      { title: 'Engineering sketches', body: 'Enough density for technical detail without the weight of 5mm engineering majors.' },
      { title: 'Exams & worksheets', body: 'Teachers generate ¼-inch grids for tests, graph-response questions, and practice sets.' },
      { title: 'Coordinate practice', body: '34 × 44 cells gives plenty of room for axes, plot points, and multi-quadrant work.' },
      { title: 'Cross-stitch charts', body: 'A 4-per-inch cell maps neatly to stitch counts for medium-size patterns.' },
      { title: 'Art & lettering', body: 'A subtle-enough grid for geometric artwork and layout planning.' },
    ],
    faqHeading: '1/4 inch graph paper — frequently asked questions',
    faqs: [
      {
        q: 'How many squares per inch does 1/4 inch graph paper have?',
        a: 'Four. Each square measures exactly 1/4 inch (6.35 mm), so an inch length spans four squares. A Letter sheet fits about 34 × 44 squares at true scale.',
      },
      {
        q: 'What is 1/4 inch graph paper used for in school?',
        a: 'It is the classic grid for math — graphing equations, plotting coordinates, geometry constructions, and exam work. Teachers also use it for worksheets where students must produce accurate graphs by hand.',
      },
      {
        q: 'Why is 1/4 inch graph paper so popular?',
        a: 'It hits a balance other sizes miss: dense enough to plot with real precision, yet not so dense that lines blur. Four squares per inch is readable at normal arm\'s length, which is why it dominates classrooms.',
      },
      {
        q: 'How do I print 1/4 inch graph paper at exact size?',
        a: 'Open the PDF and print at 100% scale with margins None and “Actual Size” selected. A ¼-inch cell will then measure exactly 6.35 mm on paper. Avoid “Fit to Page”, which rescales the grid.',
      },
      {
        q: 'Is 1/4 inch the same as 5mm graph paper?',
        a: 'Not exactly. 1/4 inch is 6.35 mm, so the squares are about 27% larger than 5mm. Towards a print finish, five 5mm squares (25mm) compare closely with four 1/4-inch squares (25.4mm) — but the printed cell sizes differ.',
      },
    ],
    relatedHeading: 'Explore the full size family',
    related: [
      { path: '/1-2-inch-graph-paper', title: '1/2 Inch Graph Paper', blurb: 'Larger cells at 2 squares per inch.' },
      { path: '/1-inch-graph-paper', title: '1 Inch Graph Paper', blurb: 'Oversized 1-square-per-inch cells.' },
      { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: 'Bold lines every 5 for counting-heavy work.' },
      { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The finer metric neighbor at ~5 per inch.' },
    ],
  },
];

export const LANDING_BY_PATH = Object.fromEntries(LANDING_PAGES.map((t) => [t.path, t]));

export const LANDING_HOME_LINKS = [
  { path: '/a4-graph-paper', title: 'A4 Graph Paper', blurb: 'The international metric standard, 210 × 297 mm, with clean 5mm squares.' },
  { path: '/5mm-graph-paper', title: '5mm Graph Paper', blurb: 'The classic metric grid — about five squares per inch for everyday precision.' },
  { path: '/dot-grid-paper', title: 'Dot Grid Paper', blurb: 'Subtle 5mm dots for bullet journals, planners, sketching, and wireframes.' },
  { path: '/isometric-graph-paper', title: 'Isometric Graph Paper', blurb: '30° triangular grids for 3D drawings and perspective sketches.' },
  { path: '/engineering-graph-paper', title: 'Engineering Graph Paper', blurb: 'Thin minor lines with a bold major line every 5 squares for technical work.' },
  { path: '/1-inch-graph-paper', title: '1 Inch Graph Paper', blurb: 'Big one-inch cells for young learners, posters, and large layouts.' },
  { path: '/1-4-inch-graph-paper', title: '1/4 Inch Graph Paper', blurb: 'The classic school grid — 4 squares per inch for math and plots.' },
  { path: '/1-2-inch-graph-paper', title: '1/2 Inch Graph Paper', blurb: 'A balanced half-inch grid, 2 squares per inch, for drawing and math.' },
];